import { useEffect, useRef } from 'react';
import { type CustomAnalyticsEvent, trackAnalyticsCustomEvent } from '../tracking';

/**
 * Calls trackAnalyticsCustomEvent when the element becomes visible for the first time.
 *
 * Example usage:
 *
 * ```tsx
 * const ref = useTrackFirstView({ eventCategory: 'Component Visible', eventAction: 'View' });
 *
 * return <div ref={ref} />;
 * ```
 */
export function useTrackFirstView({
   eventCategory,
   eventAction,
   eventName,
   eventValue,
}: CustomAnalyticsEvent) {
   const ref = useRef(null);
   const hasTrackedRef = useRef(false);

   useEffect(() => {
      if (!ref.current || hasTrackedRef.current) return;

      const observer = new IntersectionObserver(
         entries => {
            for (const entry of entries) {
               if (!entry.isIntersecting) continue;
               hasTrackedRef.current = true;
               trackAnalyticsCustomEvent({
                  eventCategory,
                  eventAction,
                  eventName,
                  eventValue,
               });
               observer.disconnect();
               break;
            }
         },
         { threshold: 0 } // 0 means as soon as one pixel is visible
      );

      observer.observe(ref.current);
      return () => {
         observer.disconnect();
      };
   }, [eventCategory, eventAction, eventName, eventValue]);

   return ref;
}
